import type { StorageAdapter, StoragePayload } from './types';

// localStorage key constants
const KEY_NAME = 'dmy_name';
const KEY_RECORDS = 'dmy_records';
const KEY_STEP = 'dmy_step';
const KEY_CHECKLIST_ID = 'dmy_checklist_id';
const KEY_FONT_SIZE = 'dmy_font_size';
// Committee comment keys follow pattern: committee_comment_{checklistId}
const COMMITTEE_COMMENT_PREFIX = 'committee_comment_';

function safeGet(key: string): string | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(key, value);
  } catch {
    // SSR or restricted env — ignore
  }
}

function safeRemove(key: string): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

/**
 * Persists all app state to the browser's localStorage.
 * This is the fallback adapter used when the remote API is unavailable.
 */
export class LocalStorageAdapter implements StorageAdapter {
  readonly isRemote = false;

  async load(): Promise<StoragePayload | null> {
    const name = safeGet(KEY_NAME) ?? '';
    const recordsRaw = safeGet(KEY_RECORDS);
    const step = safeGet(KEY_STEP) ?? '';
    const checklistId = safeGet(KEY_CHECKLIST_ID);

    // If nothing was saved yet, return null
    if (!name && !recordsRaw && !step) return null;

    const records = recordsRaw ? JSON.parse(recordsRaw) : {};

    // Load all committee comments from localStorage
    const committeeComments: Record<string, string> = {};
    if (typeof localStorage !== 'undefined') {
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(COMMITTEE_COMMENT_PREFIX)) {
          const checklistKey = k.slice(COMMITTEE_COMMENT_PREFIX.length);
          committeeComments[checklistKey] = localStorage.getItem(k) ?? '';
        }
      }
    }

    return { name, records, step, checklistId, committeeComments };
  }

  async save(payload: StoragePayload): Promise<void> {
    if (payload.name) safeSet(KEY_NAME, payload.name);
    else safeRemove(KEY_NAME);

    safeSet(KEY_RECORDS, JSON.stringify(payload.records));
    safeSet(KEY_STEP, payload.step);

    if (payload.checklistId) safeSet(KEY_CHECKLIST_ID, payload.checklistId);
    else safeRemove(KEY_CHECKLIST_ID);

    // Save committee comments
    for (const [id, comment] of Object.entries(payload.committeeComments)) {
      safeSet(`${COMMITTEE_COMMENT_PREFIX}${id}`, comment);
    }
  }

  async clear(): Promise<void> {
    safeRemove(KEY_RECORDS);
    safeRemove(KEY_STEP);
    safeRemove(KEY_CHECKLIST_ID);
    safeRemove(KEY_NAME);

    // Remove all committee comments
    if (typeof localStorage !== 'undefined') {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(COMMITTEE_COMMENT_PREFIX)) keysToRemove.push(k);
      }
      keysToRemove.forEach(k => safeRemove(k));
    }
  }

  /** Read font size preference (not part of main payload, managed separately) */
  loadFontSize(): number | null {
    const raw = safeGet(KEY_FONT_SIZE);
    if (!raw) return null;
    const n = parseInt(raw, 10);
    return isNaN(n) ? null : n;
  }

  saveFontSize(size: number): void {
    safeSet(KEY_FONT_SIZE, size.toString());
  }
}
